import React, { useLayoutEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Alert } from "reactstrap";
import { textoLicencia } from "../../helpers/fechasLicencia";

const LICENCIAS_URL = process.env.REACT_APP_LICENCIAS_URL || "https://licencias.ticketcontrol.net";

const buildRenewUrl = (companyId, licence) => {
  const url = new URL(LICENCIAS_URL + "/comprar-licencia");
  url.searchParams.set("key", companyId);
  if (licence) {
    url.searchParams.set("anpr", licence.anpr ? "1" : "0");
    url.searchParams.set("invoices", licence.invoices ? "1" : "0");
    url.searchParams.set("api", licence.api ? "1" : "0");
    url.searchParams.set("limit", String(licence.limit ?? 0));
  }
  return url.toString();
};

const LicenceExpirationBanner = () => {
  const companies = useSelector((state) => state.companies.companies) || [];
  const licences = useSelector((state) => state.companies.licences) || {};
  const wrapRef = useRef(null);

  const alerts = [];

  for (const company of companies) {
    const lic = licences[company.id];
    if (!lic || !lic.untildate) continue;

    // Con el criterio único (helpers/fechasLicencia.js): la fecha guardada es el primer
    // día SIN licencia. Antes este aviso daba la licencia por vencida recién al día
    // siguiente, y ese día decía "vencerá el ... (0d)" con el programa ya bloqueado.
    const estado = textoLicencia(lic.untildate);
    if (!estado || estado.caso === "vigente") continue;
    const expired = estado.caso === "vencida";
    alerts.push({
      company,
      licence: lic,
      diffDays: estado.dias,
      texto: estado.texto,
      type: expired ? "danger" : "warning",
      icon: expired ? "error" : "warning",
      expired,
    });
  }

  // Publica la altura del banner como CSS var (--licence-banner-h) para que
  // .page-content pueda sumarla a su padding-bottom y evitar que el contenido
  // quede tapado. Corre tras cada render y se desregistra en cleanup.
  useLayoutEffect(() => {
    const el = wrapRef.current;
    const setVar = (h) => {
      document.documentElement.style.setProperty("--licence-banner-h", `${h}px`);
    };
    if (!el) {
      setVar(0);
      return undefined;
    }
    setVar(el.offsetHeight);

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => setVar(el.offsetHeight));
      ro.observe(el);
    } else {
      const onResize = () => setVar(el.offsetHeight);
      window.addEventListener("resize", onResize);
      return () => {
        window.removeEventListener("resize", onResize);
        setVar(0);
      };
    }
    return () => {
      if (ro) ro.disconnect();
      setVar(0);
    };
  }, [alerts.length]);

  if (alerts.length === 0) return null;

  alerts.sort((a, b) => a.diffDays - b.diffDays);

  return (
    // Banner flush al bottom del viewport. El Footer se reposiciona hacia
    // arriba mediante la CSS var --licence-banner-h (ver _footer.scss),
    // quedando banner y footer apilados sin huecos. El padding-bottom de
    // .page-content también la suma para que el contenido nunca quede
    // tapado (ver _topbar.scss).
    <div
      ref={wrapRef}
      className="licence-expiration-banners"
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1020,
      }}
    >
      {alerts.map((alert) => {
        const { company, licence, expired } = alert;

        return (
          <Alert
            key={company.id}
            color={alert.type}
            className="mb-0 rounded-0 py-2 px-3"
            style={{ borderLeft: "4px solid", fontSize: 13 }}
          >
            <div className="d-flex align-items-center justify-content-between gap-2">
              <div style={{ minWidth: 0 }}>
                <span className="material-symbols-rounded me-1" style={{ fontSize: 16, verticalAlign: "middle" }}>
                  {alert.icon}
                </span>
                <strong>{company.name}</strong>
                <span className="d-none d-md-inline text-muted ms-1" style={{ fontSize: 12 }}>
                  (Key: {company.id})
                </span>
                <span> — {alert.texto}</span>
              </div>
              <a
                href={buildRenewUrl(company.id, licence)}
                target="_blank"
                rel="noopener noreferrer"
                className={`btn btn-${alert.type} btn-sm flex-shrink-0`}
                style={{ whiteSpace: "nowrap", fontSize: 12 }}
              >
                <span className="material-symbols-rounded me-1" style={{ fontSize: 14, verticalAlign: "middle" }}>
                  open_in_new
                </span>
                {expired ? "Renovar" : "Extender"}
              </a>
            </div>
          </Alert>
        );
      })}
    </div>
  );
};

export default LicenceExpirationBanner;
